import { McButton, McLoadingIndicator, McTable } from '@maersk-global/mds-react-wrapper';
import React, { Fragment, FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import TablePlaceholder from '../../common/components/TablePlaceholder/TablePlaceholder';
import { ModalContext, ModalType } from '../../common/modals/ModalProvider';
import { WeatherParams } from '../../common/service/weather/types';
import { weatherService } from '../../common/service/weather/weather-service';
import { availableParams, columns, getUnitsByName } from './consts';
import ParametersDrawer from './parameters-drawer';

type ParametersProps = {
  selectedTerminal: string;
};

const Parameters: FunctionComponent<ParametersProps> = ({ selectedTerminal }) => {
  const [parameters, setParameters] = useState([] as WeatherParams[]);
  const [paramToAdd, setParamToAdd] = useState(false);
  const [paramToEdit, setParamToEdit] = useState<WeatherParams | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const { showModal } = useContext(ModalContext);

  const service = useMemo(() => weatherService(), []);

  const fetch = async () => {
    setLoading(true);
    const resp = await service.fetchWeatherParams(selectedTerminal);
    setParameters(resp || []);
    setLoading(false);
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- by design
  }, [selectedTerminal]);

  const dataMapper = useCallback(
    (data: WeatherParams[]) =>
      data.map((h) => ({
        id: h.id,
        name: h.name,
        amount: `${h.amount} ${getUnitsByName(h.name)}`,
      })),
    [],
  );

  const eventsMapped = useMemo(() => dataMapper(parameters), [parameters, dataMapper]);

  const handleDelete = async (id: string) => {
    await service.deleteWeatherParam(id);
    fetch();
  };

  return (
    <div className="Parameters">
      {loading ? (
        <McLoadingIndicator label="Fetching data..." />
      ) : (
        <div className="Events__table-wrapper">
          <div className="Events__tilte-button">
            <h2 className="Events__title">Weather limit parameters</h2>
            {parameters.length < availableParams.length ? (
              <McButton label="Add new parameter" icon="plus" click={() => setParamToAdd(true)} />
            ) : null}
          </div>
          <McTable columns={columns} datakey="id" data={eventsMapped}>
            {parameters.map((row) => (
              <Fragment key={`${row.id}_actions`}>
                <div slot={`${row.id}_actions`}>
                  <McButton
                    fit="small"
                    variant="plain"
                    appearance="primary"
                    icon="trash"
                    click={() => showModal(ModalType.DeleteHoliday, () => () => handleDelete(row.id))}
                  >
                    Delete
                  </McButton>
                  <McButton
                    fit="small"
                    variant="plain"
                    appearance="primary"
                    icon="pencil"
                    click={() => setParamToEdit(row)}
                  >
                    Edit
                  </McButton>
                </div>
              </Fragment>
            ))}
          </McTable>
          {parameters.length ? null : <TablePlaceholder placeholder="Create a new parameter to get started" />}
        </div>
      )}

      {(paramToAdd || paramToEdit) && (
        <ParametersDrawer
          open={true}
          selectedTerminal={selectedTerminal}
          defaultValues={paramToEdit}
          action={paramToEdit ? 'edit' : 'add'}
          onSuccess={fetch}
          onClose={() => {
            setParamToEdit(undefined);
            setParamToAdd(false);
          }}
        />
      )}
    </div>
  );
};

export default Parameters;
